import {fromSeconds} from 'common/date';
import {CircleDollarSign} from 'lucide-react';
import {DateTime} from 'luxon';
import * as React from 'react';
import {Button} from 'src/Button';
import {
  formatCents,
  formatCentsShort,
  formatPeriodLabel,
  inclusiveEndDay,
} from 'src/CardBenefitsView/benefitUtils';
import {
  buildClaimQueue,
  buildOpeningSoon,
} from 'src/CardBenefitsView/benefitWindows';
import {useModals} from 'src/Modals';
import type {FullFeePeriodFragment} from '@transactions/graphql';

const OPENING_SOON_DAYS = 60;
const OPENING_SOON_SHOWN = 4;

type Props = {
  /**
   * Every fee period across every card — open windows can belong to an
   * upcoming period (calendar snap-out) or a lapsed card's final period,
   * so the queue must not scope to current cycles.
   */
  feePeriods: FullFeePeriodFragment[];
  onUpdate: () => void;
};

/**
 * Every open benefit window across every card, nearest deadline first — the
 * overview's answer to "what expires next, anywhere?". Each row records a use
 * directly via the existing create-transaction flow.
 */
export const ClaimQueue = ({feePeriods, onUpdate}: Props) => {
  const {createTransactionForBenefitModal} = useModals();
  const now = DateTime.now();
  const queue = buildClaimQueue(feePeriods, now);
  const openTotal = queue.reduce(
    (sum, item) => sum + item.window.openRemaining,
    0,
  );
  const openingSoon = buildOpeningSoon(feePeriods, now, OPENING_SOON_DAYS);

  const openingSoonLine =
    openingSoon.length > 0 ? (
      <p className="text-muted-foreground px-4 py-2 text-xs">
        Opening soon:{' '}
        {openingSoon.slice(0, OPENING_SOON_SHOWN).map((item, index) => (
          <React.Fragment key={item.benefit._id}>
            {index > 0 && ' · '}
            {fromSeconds(item.window.startDate).toLocaleString({
              month: 'short',
              day: 'numeric',
            })}{' '}
            — {item.benefit.name} {formatCentsShort(item.remaining)}
          </React.Fragment>
        ))}
        {openingSoon.length > OPENING_SOON_SHOWN &&
          ` · +${openingSoon.length - OPENING_SOON_SHOWN} more`}
      </p>
    ) : null;

  return (
    <div>
      <h3 className="text-muted-foreground mb-1.5 flex items-baseline gap-2 px-0.5 text-[11px] font-semibold tracking-wider uppercase">
        Claim next{queue.length > 0 && ` — ${formatCents(openTotal)} open`}
      </h3>
      <div className="border-border bg-card divide-border divide-y rounded-lg border">
        {queue.length === 0 && (
          <p className="text-muted-foreground px-4 py-3 text-sm">
            Nothing to claim right now.
          </p>
        )}
        {queue.map((item) => {
          const {benefit, feePeriod, window} = item;
          const usedPct =
            window.amount > 0
              ? Math.max(
                  0,
                  Math.min(
                    100,
                    Math.round((window.usedAmount / window.amount) * 100),
                  ),
                )
              : 0;
          const soon = window.daysLeft != null && window.daysLeft <= 60;
          return (
            <div
              key={`${benefit._id}-${window.startDate}`}
              className="flex items-center gap-3 px-4 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {benefit.name}{' '}
                  {/* The card attribution yields to the benefit name on
                      narrow screens */}
                  <span className="text-muted-foreground hidden text-xs font-normal sm:inline">
                    · {feePeriod.payee ?? feePeriod.account.name}
                  </span>
                </p>
                <p className="text-muted-foreground truncate text-[11px]">
                  {formatPeriodLabel(window.startDate, window.endDate)}
                  {window.usedAmount > 0 &&
                    ` · ${formatCents(window.usedAmount)} used`}
                </p>
              </div>
              <div className="bg-muted/40 hidden h-1.5 w-36 overflow-hidden rounded-full lg:block">
                <div
                  className="bg-green-500/50 h-full"
                  style={{width: `${usedPct}%`}}
                />
              </div>
              <div className="text-right tabular-nums sm:w-24">
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-300">
                  {formatCents(window.openRemaining)}
                </span>
                {window.usedAmount > 0 && (
                  <span className="text-muted-foreground block text-[10px]">
                    of {formatCentsShort(window.amount)}
                  </span>
                )}
              </div>
              <span
                className={`rounded-full px-2 py-0.5 text-center text-[11px] font-medium tabular-nums sm:w-28 ${
                  soon
                    ? 'bg-blue-500/10 text-blue-600 ring-1 ring-blue-400/40 ring-inset dark:text-blue-300'
                    : 'bg-muted/60 text-muted-foreground'
                }`}
              >
                {inclusiveEndDay(window.endDate).toLocaleString({
                  month: 'short',
                  day: 'numeric',
                })}
                {' · '}
                {window.daysLeft}d
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="shrink-0"
                title="Record a use of this benefit"
                onClick={() =>
                  createTransactionForBenefitModal.openModal({
                    benefit,
                    feePeriodPayee: feePeriod.payee,
                    onSuccess: onUpdate,
                  })
                }
              >
                <CircleDollarSign className="h-4 w-4" />
              </Button>
            </div>
          );
        })}
        {openingSoonLine}
      </div>
    </div>
  );
};
